// src/features/clinical/pages/ClinicalWorkflowPage.jsx
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaUserPlus, FaHeartbeat, FaPrescription } from 'react-icons/fa';
import PatientSearch from '../components/patient/PatientSearch';
import PatientRegistration from '../components/patient/PatientRegistration';
import VitalsForm from '../components/visit/VitalsForm';
import PrescriptionCreation from '../components/prescription/PrescriptionCreation';
import clinicalApi from '../services/clinicalApi';

const ClinicalWorkflowPage = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [currentVisit, setCurrentVisit] = useState(null);
  const [doctor] = useState({ id: 'D001', name: 'Dr. Smith' }); // This would come from auth context

  const steps = [
    { number: 1, name: 'Patient Selection', icon: FaUserPlus },
    { number: 2, name: 'Visit & Vitals', icon: FaHeartbeat },
    { number: 3, name: 'Prescription', icon: FaPrescription }
  ];

  const handlePatientSelect = async (patient) => {
    setSelectedPatient(patient);
    
    // Create a new visit
    try {
      const visitData = {
        patientId: patient.id,
        patientName: patient.name,
        visitType: 'CONSULTATION',
        doctorId: doctor.id,
        doctorName: doctor.name,
        department: 'General Medicine',
        chiefComplaint: '',
        status: 'IN_PROGRESS'
      };
      
      const response = await clinicalApi.createVisit(visitData);
      setCurrentVisit(response.data);
      setCurrentStep(2);
    } catch (error) {
      console.error('Failed to create visit:', error);
    }
  };

  const handleVitalsComplete = () => {
    setCurrentStep(3);
  };

  const handlePrescriptionComplete = () => {
    // Redirect to summary or dashboard
    window.location.href = '/clinical/dashboard';
  };

  const handleNewPatientRegistered = (patient) => {
    setSelectedPatient(patient);
    handlePatientSelect(patient);
  };

  return (
    <div className="max-w-7xl mx-auto py-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  currentStep >= step.number ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                }`}>
                  <step.icon />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">Step {step.number}</p>
                  <p className="text-sm text-gray-500">{step.name}</p>
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-24 h-1 mx-4 ${
                  currentStep > step.number ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="mt-8">
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Select or Register Patient</h2>
              <PatientSearch onSelectPatient={handlePatientSelect} />
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">OR</span>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">Register New Patient</h2>
              <PatientRegistration onSuccess={handleNewPatientRegistered} />
            </div>
          </div>
        )}

        {currentStep === 2 && currentVisit && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Visit Details - {selectedPatient?.name}
            </h2>
            <VitalsForm
              visitId={currentVisit.id}
              patientId={selectedPatient?.id}
              onComplete={handleVitalsComplete}
            />
          </div>
        )}

        {currentStep === 3 && currentVisit && selectedPatient && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Create Prescription - {selectedPatient?.name}
            </h2>
            <PrescriptionCreation
              visitId={currentVisit.id}
              patientId={selectedPatient.id}
              patientName={selectedPatient.name}
              doctorId={doctor.id}
              onComplete={handlePrescriptionComplete}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClinicalWorkflowPage;